<template>
  <div class="fixed inset-0 w-full h-full bg-black/[0.04] flex items-center justify-center z-[1000] backdrop-blur-sm" @click.self="closeModal">
    <div class="relative max-h-full overflow-auto bg-n-alpha-3 shadow-md rtl:text-right skip-context-menu rounded-xl w-[500px] max-w-[90%] max-h-[90vh] overflow-y-auto shadow-xl">
      <div class="flex justify-between items-center px-6 py-4 border-b border-n-slate-3 dark:border-n-slate-6">
        <h3 class="text-lg font-semibold m-0">{{ title }}</h3>
        <button class="z-10 ltr:right-2 rtl:left-2 top-2 inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-10 w-10 p-0 text-sm font-medium justify-center" @click="closeModal">
          <span class="i-lucide-x flex-shrink-0"></span>
        </button>
      </div>
      
      <div class="p-6">
        <div class="flex gap-4 items-start">
          <div class="w-full">
            <!-- Dynamic content based on type prop -->
            <template v-if="type === 'disable'">
              <div class="space-y-5">
                <!-- Enable/Disable Toggle -->
                <div class="mb-5">
                  <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Ativar/Desativar</label>
                  <button
                    type="button"
                    @click="toggleActive"
                    class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1 flex justify-between items-center cursor-pointer"
                  >
                    <span class="text-sm font-medium text-n-slate-12">{{ isActive ? 'Ativar' : 'Desativar' }}</span>
                    <input 
                      type="checkbox"
                      :checked="isActive"
                      class="w-4 h-4 text-n-brand bg-n-background border-n-slate-4 rounded focus:ring-n-brand dark:focus:ring-n-brand dark:ring-offset-slate-800 focus:ring-2 dark:bg-slate-700 dark:border-slate-600 pointer-events-none"
                      @click.stop
                    />
                  </button>
                </div>

                <!-- Inbox Selection -->
                <div>
                  <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Selecionar inbox</label>
                  <div class="relative inbox-dropdown">
                    <button
                      ref="inboxTrigger"
                      @click.stop="isActive ? toggleInboxDropdown() : null"
                      :disabled="!isActive"
                      :class="{
                        'w-full px-3 py-2.5 border rounded-lg text-sm flex justify-between items-center text-left': true,
                        'border-n-slate-4 dark:border-n-slate-7 bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1 cursor-pointer': isActive,
                        'border-n-slate-3 dark:border-n-slate-6 bg-n-slate-2 dark:bg-n-slate-8 text-n-slate-9 cursor-not-allowed': !isActive
                      }"
                      type="button"
                    >
                      <div class="flex items-center overflow-hidden">
                        <div 
                          class="w-6 h-6 flex items-center justify-center rounded-full mr-2 flex-shrink-0"
                          :class="getSelectedInboxDisplayClasses()"
                        >
                          <i :class="getSelectedInboxDisplayIcon() + ' text-sm'"></i>
                        </div>

                        <span class="truncate">
                          {{ getSelectedInboxDisplayText() }}
                        </span>
                      </div>
                      <i class="i-lucide-chevron-down ml-2 flex-shrink-0 transition-transform" :class="{ 'rotate-180': showInboxDropdown }"></i>
                    </button>
                  </div>
                  
                  <!-- Dropdown Options - Teleported -->
                  <Teleport to="body">
                    <div
                      v-if="showInboxDropdown"
                      v-on-clickaway="closeInboxDropdown"
                      class="dropdown-pane dropdown-pane--open absolute w-64 bg-white dark:bg-n-slate-2 shadow-lg rounded-lg z-[10050] border border-n-slate-4 dark:border-n-slate-7"
                      :style="inboxDropdownStyles"
                    >
                      <!-- Todos option -->
                      <div class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
                            @click.stop="selectInbox('todos')">
                        <div class="flex items-center">
                          <div class="w-8 h-8 flex items-center justify-center rounded-full mr-3 bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                            <i class="i-lucide-globe"></i>
                          </div>
                          <div>
                            <div class="text-sm font-medium">Todos</div>
                            <div class="text-xs text-slate-500">Disponível para todos os inboxes</div>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Grouped Inboxes -->
                      <div class="max-h-60 overflow-y-auto">
                        <template v-for="(item, index) in groupedInboxOptions" :key="index">
                          <!-- Group Header -->
                          <div v-if="item.type === 'header'"
                                class="px-3 py-1 text-xs font-semibold bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-y border-slate-100 dark:border-slate-700 sticky top-0 z-10">
                            {{ item.label }}
                          </div>
                          
                          <!-- Inbox Option -->
                          <div v-else
                                class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
                                @click.stop="selectInbox(String(item.inbox_id))">
                            <div class="flex items-center">
                              <div class="w-8 h-8 flex items-center justify-center rounded-full mr-3 bg-grass-300 text-grass-900">
                                <i :class="getInboxTypeIcon(item.channel_type)"></i>
                              </div>
                              
                              <div>
                                <div class="text-sm font-medium">
                                  {{ getInboxDisplayName(item) }}
                                </div>
                                <div v-if="item.integration_type === 'official'" class="text-xs text-grass-700 dark:text-grass-400">
                                  API Oficial
                                </div>
                                <div v-else-if="item.integration_type === 'evolution'" class="text-xs text-grass-700 dark:text-grass-400">
                                  Não Oficial
                                </div>
                              </div>
                            </div>
                          </div>
                        </template>
                      </div>
                    </div>
                  </Teleport>
                </div>

                <!-- Explanation Text -->
                <div class="text-xs text-n-slate-10 leading-normal">
                  <p class="mb-2">
                    Os produtos desativados não serão recomendados aos clientes e não serão utilizados pela I.A como material de conhecimento.
                  </p>
                  <p class="mb-0 text-woot-600 font-medium">
                    {{ isActive ? `Você está alterando ${data.length} cadastro${data.length !== 1 ? 's' : ''} selecionado${data.length !== 1 ? 's' : ''}.` : `Você está desativando ${data.length} cadastro${data.length !== 1 ? 's' : ''} selecionado${data.length !== 1 ? 's' : ''}.` }}
                  </p>
                </div>
              </div>
            </template>
            
            <template v-else>
              <slot name="content">
                <p class="text-sm text-n-slate-12 m-0 leading-6 break-words">Conteúdo padrão do modal.</p>
              </slot>
            </template>
          </div>
        </div>
        <p v-if="error" class="text-red-500 text-sm mt-1">{{ error }}</p>
      </div>
      
      <div class="flex justify-end gap-3 px-6 py-4 border-t border-n-slate-3 dark:border-n-slate-6">
        <button 
          type="button" 
          class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center" 
          @click="closeModal"
        >
          Cancelar
        </button>
        <button 
          type="button" 
          class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-woot-600 text-white hover:enabled:bg-woot-700 focus-visible:bg-woot-700 outline-transparent h-10 px-4 text-sm font-medium justify-center" 
          @click="confirmAction"
        >
          <span v-if="loading" class="animate-spin i-lucide-loader"></span>
          <span>{{ loading ? 'Processando...' : actionText }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import wootAPI from '@/v3/api/apiClient';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'GeneralProductModal',
  props: {
    title: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      default: 'default',
    },
    actionText: {
      type: String,
      required: true,
    },
    data: {
      type: [Object, Array],
      default: () => ({}),
    },
    connectedInboxes: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close', 'action', 'success'],
  setup(props, { emit }) {
    const store = useStore();
    const loading = ref(false);
    const error = ref(null);
    
    // Inbox functionality
    const inboxOptions = ref([]);
    const showInboxDropdown = ref(false);
    const selectedInbox = ref('todos');
    
    // Enable/Disable functionality
    const isActive = ref(true);
    
    // Template ref for dropdown positioning
    const inboxTrigger = ref(null);

    const closeModal = () => {
      showInboxDropdown.value = false;
      emit('close');
    };

    const confirmAction = async () => {
      if (props.type === 'disable') {
        loading.value = true;
        error.value = null;
        
        try {
          const headers = getAuthHeaders();
          
          // Determine what to send based on the toggle state
          const inboxValue = isActive.value ? selectedInbox.value : 'disabled';
          
          const response = await wootAPI.patch('/api/v1/catalogs/batch_update', {
            product_ids: props.data,
            selected_inbox: inboxValue
          }, { headers });
          
          console.log('Batch update successful:', response.data);
          
          // Show success toast based on the action performed
          const successMessage = isActive.value 
            ? 'Produtos ativados com sucesso' 
            : 'Produtos desativados com sucesso';
          useAlert(successMessage, 'success');
          
          // Emit success with the updated data
          emit('action', { 
            productIds: props.data,
            success: true,
            message: response.data.message
          });
          emit('success', { 
            productIds: props.data,
            success: true,
            message: response.data.message
          });
        } catch (err) {
          console.error('Error updating products:', err);
          error.value = err.response?.data?.error || 'Erro ao atualizar produtos';
          
          // Show generic error toast
          useAlert('Erro ao atualizar produtos', 'error');
          
          loading.value = false;
          return; // Don't close modal on error
        } finally {
          loading.value = false;
        }
      } else {
        emit('action', props.data);
        emit('success', props.data);
      }
    };

    // Inbox helper functions
    function getCookie(name) {
      const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
      return match ? decodeURIComponent(match[2]) : null;
    }

    function getAuthHeaders() {
      const cookie = getCookie('cw_d_session_info');
      if (!cookie) return {};
      try {
        const session = JSON.parse(cookie);
        return {
          'access-token': session['access-token'],
          client: session.client,
          uid: session.uid
        };
      } catch {
        return {};
      }
    }

    const groupedInboxOptions = computed(() => {
      const result = [];
      const inboxes = inboxOptions.value || [];
      
      const whatsappEvolution = [];
      const whatsappOfficial = [];
      const otherChannels = [];
      
      inboxes.forEach(inbox => {
        const name = (inbox.inbox_name || inbox.name || '').toLowerCase();
        
        if (inbox.integration_type === 'official' || 
            name.includes('api oficial') ||
            name.includes('cloud') ||
            name.includes('official')) {
          whatsappOfficial.push({...inbox, integration_type: 'official'});
        }
        else if (inbox.integration_type === 'evolution' || name.includes('evolution') || name.includes('unofficial')) {
          whatsappEvolution.push({...inbox, integration_type: 'evolution'});
        }
        else {
          otherChannels.push(inbox);
        }
      });
      
      if (whatsappEvolution.length > 0) {
        result.push({ type: 'header', label: 'WHATSAPP NÃO OFICIAL' });
        result.push(...whatsappEvolution);
      }
      
      if (whatsappOfficial.length > 0) {
        result.push({ type: 'header', label: 'WHATSAPP API OFICIAL' });
        result.push(...whatsappOfficial);
      }
      
      if (otherChannels.length > 0) {
        result.push({ type: 'header', label: 'OUTROS CANAIS' });
        result.push(...otherChannels);
      }
      
      return result;
    });

    const getInboxTypeIcon = (type) => {
      switch (type?.toLowerCase()) {
        case 'whatsapp':
          return 'i-lucide-message-circle';
        case 'facebook':
          return 'i-lucide-facebook';
        case 'twitter':
          return 'i-lucide-twitter';
        case 'email':
          return 'i-lucide-mail';
        case 'web':
          return 'i-lucide-globe';
        case 'telegram':
          return 'i-lucide-send';
        default:
          return 'i-lucide-message-square';
      }
    };

    // Computed styles for teleported dropdown
    const inboxDropdownStyles = computed(() => {
      if (!inboxTrigger.value || !showInboxDropdown.value) return {};
      const rect = inboxTrigger.value.getBoundingClientRect();
      return {
        left: `${rect.left}px`,
        top: `${rect.bottom + 2}px`
      };
    });

    const toggleInboxDropdown = () => {
      showInboxDropdown.value = !showInboxDropdown.value;
    };

    const closeInboxDropdown = () => {
      showInboxDropdown.value = false;
    };

    const selectInbox = (inboxId) => {
      selectedInbox.value = inboxId;
      closeInboxDropdown();
    };

    const toggleActive = () => {
      isActive.value = !isActive.value;
    };

    const getInboxName = (inboxId) => {
      if (inboxId === 'todos') return 'Todos';
      
      // First try to get display_name from store
      const displayName = store.getters['inboxMetadata/getInboxDisplayName'](inboxId);
      if (displayName) return displayName;
      
      // Fallback to inbox_name from inboxOptions
      const inbox = inboxOptions.value.find(inbox => 
        String(inbox.inbox_id) === inboxId
      );
      
      return inbox ? inbox.inbox_name : 'Inbox desconhecido';
    };
    
    const getInboxDisplayName = (inbox) => {
      // First try to get display_name from store
      const displayName = store.getters['inboxMetadata/getInboxDisplayName'](inbox.inbox_id || inbox.id);
      if (displayName) return displayName;
      
      // Fallback to inbox_name or name
      return inbox.inbox_name || inbox.name || `Inbox ${inbox.inbox_id || inbox.id}`;
    };

    const getSelectedInboxDisplayText = () => {
      return getInboxName(selectedInbox.value);
    };

    const getSelectedInboxDisplayIcon = () => {
      if (selectedInbox.value === 'todos') {
        return 'i-lucide-globe';
      }
      
      const inbox = inboxOptions.value.find(inbox => 
        String(inbox.inbox_id) === selectedInbox.value
      );
      return inbox ? getInboxTypeIcon(inbox.channel_type) : 'i-lucide-inbox';
    };

    const getSelectedInboxDisplayClasses = () => {
      if (selectedInbox.value === 'todos') {
        return 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300';
      }
      
      // All individual inboxes get green color
      return 'bg-grass-300 text-grass-900';
    };

    const loadInboxes = () => {
      if (props.type !== 'disable') return;
      
      try {
        // Use the connected inboxes passed as prop - no transformation needed
        // as the data is already in the correct format from load_inboxes endpoint
        const connectedInboxes = props.connectedInboxes || [];
        
        inboxOptions.value = connectedInboxes;
        console.log('Using connected inboxes from parent:', connectedInboxes.length);
      } catch (err) {
        console.error('Falha ao carregar inboxes:', err);
        inboxOptions.value = [];
      }
    };

    onMounted(() => {
      loadInboxes();
    });

    return {
      loading,
      error,
      closeModal,
      confirmAction,
      // Inbox functionality
      showInboxDropdown,
      selectedInbox,
      groupedInboxOptions,
      getInboxTypeIcon,
      toggleInboxDropdown,
      closeInboxDropdown,
      selectInbox,
      getInboxDisplayName,
      getSelectedInboxDisplayText,
      getSelectedInboxDisplayIcon,
      getSelectedInboxDisplayClasses,
      // Enable/Disable functionality
      isActive,
      toggleActive,
      // Template refs
      inboxTrigger,
      // Dropdown positioning
      inboxDropdownStyles,
    };
  },
};
</script>